import * as z from 'zod'
import { zodQs } from '../utils'

export const NotificationType = {
    DocumentError: 'document_error',
    ApiKeyExpiring: 'api_key_expiring',
    Organization: 'organization',
    PasswordResetRequested: 'password_reset_requested',
    /** A sync took documents out because the remote no longer has them. */
    DocumentSyncRemoved: 'document_sync_removed',
    ChatWarning: 'chat_warning',
    ChatBlocked: 'chat_blocked',
    Generic: 'generic',
} as const

export type NotificationType =
    (typeof NotificationType)[keyof typeof NotificationType]

const NotificationTypeSchema = z.string() as z.ZodType<string>

export const NotificationSchema = z.object({
    id: z.string(),
    organizationId: z.string().nullable(),
    organizationName: z.string().nullable().optional(),
    userId: z.string().nullable(),
    userEmail: z.string().nullable().optional(),
    type: NotificationTypeSchema,
    title: z.string(),
    body: z.string().nullable(),
    groupKey: z.string().nullable(),
    groupCount: z.number().int(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    scheduledAt: z.coerce.date().nullable(),
    readAt: z.coerce.date().nullable(),
    deletedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
})

export const NotificationsQuerystringSchema = z.object({
    ...zodQs.pagination(),
    ...zodQs.sort(['createdAt', 'updatedAt', 'scheduledAt']),
    ...zodQs.filters(['type', 'readAt', 'scheduledAt']), // scheduledAt: 'future' | 'past' | omit for all
    ...zodQs.ids(),
})

export const NotificationMarkReadSchema = NotificationSchema.pick({
    id: true,
    organizationId: true,
    userId: true,
})

export const NotificationDeleteSchema = z.object({
    id: z.string().uuid(),
})

export const NotificationCreateDtoSchema = z.object({
    organizationId: z.uuid().nullable().optional(),
    userId: z.uuid().nullable().optional(),
    type: NotificationTypeSchema.default(NotificationType.Generic),
    title: z.string().min(1).max(500),
    body: z.string().max(2000).nullable().optional(),
    groupKey: z.string().max(255).nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).nullable().optional(),
    scheduledAt: z.coerce.date().nullable().optional(),
})

export const FrontendNotificationTypes = [
    NotificationType.Generic,
    NotificationType.Organization,
] as const

export const NotificationCreateFrontendDtoSchema =
    NotificationCreateDtoSchema.extend({
        type: z
            .enum(FrontendNotificationTypes)
            .default(NotificationType.Generic),
    })

export type NotificationCreateFrontendDto = z.infer<
    typeof NotificationCreateFrontendDtoSchema
>

export const NotificationUnreadCountSchema = z.object({
    count: z.number().int(),
})

export const UserNotificationSchema = z.object({
    id: z.string(),
    notificationId: z.string(),
    userId: z.string(),
    readAt: z.coerce.date().nullable(),
    deletedAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date(),
})

export type Notification = z.infer<typeof NotificationSchema>
export type NotificationsQuerystring = z.infer<
    typeof NotificationsQuerystringSchema
>
export type NotificationMarkRead = z.infer<typeof NotificationMarkReadSchema>
export type NotificationCreateDto = z.infer<typeof NotificationCreateDtoSchema>
export type NotificationUnreadCount = z.infer<
    typeof NotificationUnreadCountSchema
>
export type UserNotification = z.infer<typeof UserNotificationSchema>
export type NotificationDelete = z.infer<typeof NotificationDeleteSchema>
